import { useEffect, useRef, useState } from 'react';

interface StatItem {
  value: number;
  suffix: string;
  label: string;
  sublabel: string;
  icon: string;
  decimals?: number;
}

const stats: StatItem[] = [
  {
    value: 47,
    suffix: '',
    label: 'Bewertungen',
    sublabel: 'auf Google & Instagram',
    icon: 'ri-chat-smile-3-line',
  },
  {
    value: 5.0,
    suffix: ' ★',
    label: 'Sterne',
    sublabel: 'Durchschnittsbewertung',
    icon: 'ri-star-smile-line',
    decimals: 1,
  },
  {
    value: 100,
    suffix: '+',
    label: 'Gäste',
    sublabel: 'strahlen bereits',
    icon: 'ri-user-heart-line',
  },
  {
    value: 19,
    suffix: '+',
    label: 'Jahre Erfahrung',
    sublabel: 'in der Dentalbranche',
    icon: 'ri-award-line',
  },
];

function useCountUp(target: number, duration: number, active: boolean, decimals = 0) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;
    let start: number | null = null;
    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(parseFloat((eased * target).toFixed(decimals)));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, target, duration, decimals]);

  return count;
}

interface StatCardProps {
  stat: StatItem;
  active: boolean;
  index: number;
}

function StatCard({ stat, active, index }: StatCardProps) {
  const count = useCountUp(stat.value, 1800, active, stat.decimals ?? 0);

  return (
    <div
      className="flex flex-col items-center text-center px-6 py-10 md:py-14 relative group"
      style={{
        animationDelay: `${index * 120}ms`,
        opacity: active ? 1 : 0,
        transform: active ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s ease ${index * 120}ms, transform 0.6s ease ${index * 120}ms`,
      }}
    >
      {/* Icon */}
      <div className="w-14 h-14 flex items-center justify-center bg-rose/12 rounded-2xl mb-5 group-hover:bg-rose/20 transition-colors duration-300">
        <i className={`${stat.icon} text-rose text-2xl`} />
      </div>

      {/* Number */}
      <div className="flex items-end justify-center gap-0.5 mb-1">
        <span className="font-serif text-5xl md:text-6xl font-medium text-charcoal leading-none tabular-nums">
          {stat.decimals ? count.toFixed(stat.decimals) : Math.floor(count)}
        </span>
        <span className="font-serif text-3xl md:text-4xl font-medium text-rose leading-none mb-1">
          {stat.suffix}
        </span>
      </div>

      {/* Label */}
      <p className="font-sans text-base font-semibold text-charcoal mb-1">{stat.label}</p>
      <p className="font-sans text-xs text-charcoal/50">{stat.sublabel}</p>

      {/* Divider (not on last) */}
      <div className="hidden lg:block absolute right-0 top-1/4 bottom-1/4 w-px bg-charcoal/8 last:hidden" />
    </div>
  );
}

export default function StatsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-white py-4 md:py-6">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section label */}
        <div className="text-center mb-2">
          <span className="font-sans text-xs tracking-widest uppercase text-rose/70 font-semibold">
            Vertrauen in Zahlen
          </span>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-charcoal/8 border border-charcoal/8 rounded-3xl overflow-hidden">
          {stats.map((stat, i) => (
            <StatCard key={stat.label} stat={stat} active={active} index={i} />
          ))}
        </div>

        {/* Stars row */}
        <div
          className="flex items-center justify-center gap-2 mt-5"
          style={{
            opacity: active ? 1 : 0,
            transform: active ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 0.6s ease 600ms, transform 0.6s ease 600ms',
          }}
        >
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <span key={s} className="w-4 h-4 flex items-center justify-center">
                <i className="ri-star-fill text-amber-400 text-sm" />
              </span>
            ))}
          </div>
          <span className="font-sans text-xs text-charcoal/50">
            Alle Bewertungen verifiziert · Köln &amp; Umgebung
          </span>
        </div>
      </div>
    </section>
  );
}
